import React, { createContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import ENVIRONMENT from '../../../environments';

const BulkTaskContext = createContext();

const POLL_INTERVAL = 3000; // 3 seconds
const RECENT_WINDOW = 10 * 60 * 1000; // 10 minutes
const SYNC_KEY = 'bulk_tasks_updated';
const DOWNLOAD_LOCK_PREFIX = 'bulk_download_opened_';
const DOWNLOAD_LOCK_TTL = 10 * 60 * 1000; // 10 minutes

// Cross-tab lock so only one tab auto-opens the download when a bulk action completes.
// Returns true if this tab claimed the lock; false if another tab already did.
const claimDownloadLock = (bulkActionId) => {
  try {
    const key = DOWNLOAD_LOCK_PREFIX + bulkActionId;
    const existing = localStorage.getItem(key);
    if (existing) {
      const ts = Number(existing);
      if (Number.isFinite(ts) && Date.now() - ts < DOWNLOAD_LOCK_TTL) return false;
    }
    localStorage.setItem(key, String(Date.now()));
    return true;
  } catch (_) {
    return true;
  }
};

let taskIdCounter = 0;

// Notify other browser tabs with task data so they don't need an API round-trip
const notifyOtherTabs = (task) => {
  try {
    localStorage.setItem(SYNC_KEY, JSON.stringify({ ts: Date.now(), task: task || null }));
  } catch (_) {}
};

export const BulkTaskProvider = ({ children }) => {
  const [tasks, setTasks] = useState([]);
  const pollTimersRef = useRef({});
  const pollingBulkIdsRef = useRef(new Set());

  const updateTask = useCallback((taskId, updates) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, ...updates } : t))
    );
  }, []);

  const stopPolling = useCallback((taskId, bulkActionId) => {
    if (pollTimersRef.current[taskId]) {
      clearInterval(pollTimersRef.current[taskId]);
      delete pollTimersRef.current[taskId];
    }
    if (bulkActionId) pollingBulkIdsRef.current.delete(bulkActionId);
  }, []);

  const startPolling = useCallback((taskId, bulkActionId, companyId, username, imageCount) => {
    // Guard by bulkActionId to prevent duplicate polling across tabs/re-fetches
    if (pollingBulkIdsRef.current.has(bulkActionId)) return;
    pollingBulkIdsRef.current.add(bulkActionId);

    const startTime = Date.now();
    const TIMEOUT = 15 * 60 * 1000; // 15 minutes
    const total = imageCount || 0;
    // Fake progress: advance towards total-1, then hold until server confirms
    let fakeProcessed = 0;
    const fakeStep = Math.max(1, Math.ceil(total / 8));

    const poll = async () => {
      // Timeout — stop after 15 minutes
      if (Date.now() - startTime > TIMEOUT) {
        updateTask(taskId, {
          status: 'failed',
          label: 'Timed out waiting for download',
        });
        stopPolling(taskId, bulkActionId);
        return;
      }

      // Advance fake progress (never reaches total — holds at total-1)
      if (total > 0 && fakeProcessed < total - 1) {
        fakeProcessed = Math.min(fakeProcessed + fakeStep, total - 1);
        updateTask(taskId, {
          processedItems: fakeProcessed,
          totalItems: total,
        });
      }

      // Poll the real server for actual status
      try {
        const res = await axios.post(ENVIRONMENT.AI_MODEL_HELPER, {
          action: 'get_bulk_action_status',
          bulk_action_id: bulkActionId,
          company_id: Number(companyId),
          created_by: username,
        });

        const data = res.data;
        if (!data || data.result !== 'success') return;

        const { status, processed_items, total_items, download_url, error_message } = data;

        if (status === 'completed') {
          updateTask(taskId, {
            status: 'completed',
            processedItems: total_items || total,
            totalItems: total_items || total,
            downloadUrl: download_url || null,
            label: `${total_items || total} file${(total_items || total) !== 1 ? 's' : ''} ready`,
          });
          stopPolling(taskId, bulkActionId);
          notifyOtherTabs();
          if (download_url && claimDownloadLock(bulkActionId)) {
            window.open(download_url, '_blank');
          }
        } else if (status === 'failed') {
          updateTask(taskId, {
            status: 'failed',
            label: error_message || 'Download failed',
          });
          stopPolling(taskId, bulkActionId);
          notifyOtherTabs();
        } else if (status === 'in-progress' && processed_items != null) {
          // Use real progress if server reports it and it's ahead of fake
          const realProcessed = Math.min(processed_items, (total_items || total) - 1);
          if (realProcessed > fakeProcessed) {
            fakeProcessed = realProcessed;
            updateTask(taskId, {
              processedItems: fakeProcessed,
              totalItems: total_items || total,
            });
          }
        }
      } catch (error) {
        console.error('Bulk task polling error:', error);
        // Don't stop on network errors — keep trying until timeout
      }
    };

    // Poll immediately, then on interval
    poll();
    pollTimersRef.current[taskId] = setInterval(poll, POLL_INTERVAL);
  }, [updateTask, stopPolling]);

  const addTask = useCallback((task) => {
    const id = ++taskIdCounter;
    const newTask = {
      id,
      title: task.title || 'Processing',
      label: task.label || '',
      status: 'in_progress',
      imageCount: task.imageCount || 0,
      processedItems: 0,
      totalItems: task.imageCount || 0,
      bulkActionId: task.bulkActionId || null,
      downloadUrl: null,
      createdAt: Date.now(),
    };
    setTasks((prev) => [...prev, newTask]);
    notifyOtherTabs(newTask);

    // Start polling if we have a bulkActionId
    if (task.bulkActionId) {
      const companyId = localStorage.getItem('company_id');
      const username = localStorage.getItem('username');
      startPolling(id, task.bulkActionId, companyId, username, task.imageCount || 0);
    }

    return id;
  }, [startPolling]);

  const removeTask = useCallback((taskId) => {
    stopPolling(taskId);
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
  }, [stopPolling]);

  const clearCompleted = useCallback(() => {
    setTasks((prev) => {
      prev.forEach((t) => {
        if (t.status !== 'in_progress') stopPolling(t.id);
      });
      return prev.filter((t) => t.status === 'in_progress');
    });
  }, [stopPolling]);

  // Normalize response — API returns either a raw array or { actions: [...] }
  const extractActions = (res) => {
    const data = res.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.actions)) return data.actions;
    return [];
  };

  // Fetch tasks from the API and add them to state
  const fetchAndRestoreTasks = useCallback(() => {
    const companyId = localStorage.getItem('company_id');
    const username = localStorage.getItem('username');
    if (!companyId || !username) return;

    const basePayload = {
      action: 'get_user_bulk_actions',
      company_id: Number(companyId),
      created_by: username,
    };

    const inProgressReq = axios.post(ENVIRONMENT.AI_MODEL_HELPER, { ...basePayload, status: 'in-progress' });
    const completedReq = axios.post(ENVIRONMENT.AI_MODEL_HELPER, { ...basePayload, status: 'completed' });

    Promise.allSettled([inProgressReq, completedReq]).then(([inProgressRes, completedRes]) => {
      // Restore in-progress tasks — resume polling
      if (inProgressRes.status === 'fulfilled') {
        const actions = extractActions(inProgressRes.value);
        actions.forEach((action) => {
          const bulkActionId = action.id;
          if (!bulkActionId) return;

          const totalItems = action.total_items || 0;
          const id = ++taskIdCounter;

          // Check for duplicates inside the updater to read current state synchronously
          setTasks((prev) => {
            if (prev.some((t) => t.bulkActionId === bulkActionId)) return prev;
            return [...prev, {
              id,
              title: 'Preparing download',
              label: `Zipping ${totalItems} file${totalItems !== 1 ? 's' : ''}`,
              status: 'in_progress',
              imageCount: totalItems,
              processedItems: action.processed_items || 0,
              totalItems,
              bulkActionId,
              downloadUrl: null,
              createdAt: Date.now(),
            }];
          });

          startPolling(id, bulkActionId, companyId, username, totalItems);
        });
      }

      // If no in-progress tasks, show the single latest completed task with its download URL
      if (inProgressRes.status !== 'fulfilled' || extractActions(inProgressRes.value).length === 0) {
        if (completedRes.status === 'fulfilled') {
          const actions = extractActions(completedRes.value);
          const now = Date.now();
          const latest = actions
            .filter((a) => {
              const ts = a.last_modified || a.created_on;
              return ts && (now - new Date(ts).getTime() < RECENT_WINDOW);
            })
            .sort((a, b) => new Date(b.last_modified || b.created_on) - new Date(a.last_modified || a.created_on))[0];

          if (latest) {
            const bulkActionId = latest.id;
            const totalItems = latest.total_items || 0;
            const id = ++taskIdCounter;

            axios.post(ENVIRONMENT.AI_MODEL_HELPER, {
              action: 'get_bulk_action_status',
              bulk_action_id: bulkActionId,
              company_id: Number(companyId),
              created_by: username,
            }).then((statusRes) => {
              const downloadUrl = statusRes.data?.download_url || null;
              setTasks((prev) => {
                if (prev.some((t) => t.bulkActionId === bulkActionId)) return prev;
                return [...prev, {
                  id,
                  title: 'Download ready',
                  label: `${totalItems} file${totalItems !== 1 ? 's' : ''} ready`,
                  status: 'completed',
                  imageCount: totalItems,
                  processedItems: totalItems,
                  totalItems,
                  bulkActionId,
                  downloadUrl,
                  createdAt: new Date(latest.last_modified || latest.created_on).getTime(),
                }];
              });
            }).catch(() => {
              // Can't get download URL — don't show a misleading completed task
              setTasks((prev) => {
                return prev;
              });
            });
          }
        }
      }
    }).catch((err) => {
      console.error('Failed to restore bulk actions:', err);
    });
  }, [startPolling]);

  // On mount: restore tasks from API
  const hasRestoredRef = useRef(false);
  useEffect(() => {
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;
    fetchAndRestoreTasks();
  }, [fetchAndRestoreTasks]);

  // Listen for task updates from other browser tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== SYNC_KEY || !e.newValue) return;

      try {
        const { task } = JSON.parse(e.newValue);
        if (task && task.bulkActionId) {
          // Another tab sent a task directly — add it and start polling
          const id = ++taskIdCounter;
          setTasks((prev) => {
            if (prev.some((t) => t.bulkActionId === task.bulkActionId)) return prev;
            return [...prev, { ...task, id }];
          });

          if (task.status === 'in_progress') {
            const companyId = localStorage.getItem('company_id');
            const username = localStorage.getItem('username');
            startPolling(id, task.bulkActionId, companyId, username, task.imageCount || 0);
          }
        } else {
          // No task data — fall back to API fetch (e.g. completion/failure notifications)
          fetchAndRestoreTasks();
        }
      } catch (_) {
        fetchAndRestoreTasks();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [fetchAndRestoreTasks, startPolling]);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      Object.values(pollTimersRef.current).forEach(clearInterval);
    };
  }, []);

  return (
    <BulkTaskContext.Provider
      value={{
        tasks,
        addTask,
        updateTask,
        removeTask,
        clearCompleted,
      }}
    >
      {children}
    </BulkTaskContext.Provider>
  );
};

export default BulkTaskContext;
